import {
  CancelPartialRefundOffer,
  ChooseComplaintResolution,
  CompleteDeliveredTransaction,
  DeclinePartialRefundOffer,
  RefundProgress,
  RequestReturn,
  ResolveComplaint,
  ViewComplaint,
  RequestReturnDetailsProvider,
  ViewIssueDetails,
  AcceptPartialRefundOffer,
  ComplaintActionConversationWrapper,
} from '@marketplace-web/complaints/complaints-feature'
import {
  AcceptChangeDeliveryOptionRequest,
  AgreeExtendShippingDeadline,
  CancelChangeDeliveryOptionRequest,
  CancelShippingDeadlineExtension,
  CancellationConfirm,
  CancellationDecline,
  CompleteTransaction,
  CustomShipmentInstructions,
  DeclineShippingDeadlineExtension,
  DownloadShippingLabel,
  ExtendShippingDeadline,
  GetShippingLabel,
  MarkAsDelivered,
  MarkAsReturnedToSender,
  MarkAsShipped,
  OpenQrCode,
  TrackShipment,
  ViewDeliveryInstructions,
  WeHaveMet,
} from '@marketplace-web/conversation-shipping/conversation-shipping-feature'
import {
  ConfirmComplaintResolved,
  RefuseComplaintResolved,
  ReportProblem,
} from '@marketplace-web/help-center-conversation-support/conversation-support-feature'
import { ContactVintedSupport } from '@marketplace-web/help-center-conversation-support/conversational-chatbot-feature'
import { PromotedClosetPrecheckout, PromotedClosetStats } from '@marketplace-web/vas/vas-feature'
import { ConfirmPackageSize, UpdateBundle } from '@marketplace-web/bundles/items-bundle-feature'
import { ActivateBalance } from '@marketplace-web/balance-activation/balance-activation-feature'
import { GoToWallet } from '@marketplace-web/wallet/wallet-feature'
import { ReuploadItem } from '@marketplace-web/conversation-reupload-item/conversation-reupload-item-feature'
import { AppealTheDecision } from '@marketplace-web/moderation/conversation-moderation-feature'
import {
  DenyAutomatedSupportFeedback,
  DenyHelpCenterFeedback,
  ProvideAutomatedSupportFeedback,
  ResolveHelpCenterFeedback,
  LeaveFeedback,
  CesFeedbackModal,
} from '@marketplace-web/conversation-feedback/conversation-feedback-feature'
import {
  ConversationActionProps,
  ConversationActionType,
} from '@marketplace-web/messaging/conversation-action-data'

import UploadItem from '../UploadItem'

type Props = ConversationActionProps & {
  isCesModalVisibilityFixEnabled: boolean
}

const ConversationAction = (props: Props) => {
  const { action, isCesModalVisibilityFixEnabled } = props

  switch (action) {
    case ConversationActionType.CustomShipment:
    case ConversationActionType.UntrackedShipment:
      return <CustomShipmentInstructions {...props} />
    case ConversationActionType.ConfirmPackageSize:
      return <ConfirmPackageSize {...props} />
    case ConversationActionType.ReuploadItem:
      return <ReuploadItem {...props} />
    case ConversationActionType.ResolveComplaint:
      return (
        <ComplaintActionConversationWrapper>
          {({ transactionId, complaintId, userSide }) => (
            <ResolveComplaint
              {...props}
              transactionId={transactionId}
              complaintId={complaintId}
              userSide={userSide}
            />
          )}
        </ComplaintActionConversationWrapper>
      )
    case ConversationActionType.ViewComplaint:
      return (
        <ComplaintActionConversationWrapper>
          {({ transactionId, complaintId }) => (
            <ViewComplaint {...props} transactionId={transactionId} complaintId={complaintId} />
          )}
        </ComplaintActionConversationWrapper>
      )
    case ConversationActionType.ViewIssueDetails:
      return <ViewIssueDetails {...props} />
    case ConversationActionType.DownloadShippingLabel:
      return <DownloadShippingLabel {...props} />
    case ConversationActionType.CancellationConfirm:
      return <CancellationConfirm {...props} />
    case ConversationActionType.CancellationDecline:
      return <CancellationDecline {...props} />
    case ConversationActionType.TrackShipment:
      return <TrackShipment {...props} />
    case ConversationActionType.MarkAsDelivered:
      return <MarkAsDelivered {...props} />
    case ConversationActionType.MarkAsShipped:
      return <MarkAsShipped {...props} />
    case ConversationActionType.GetShippingLabel:
      return <GetShippingLabel {...props} />
    case ConversationActionType.GoToWallet:
      return <GoToWallet {...props} />
    case ConversationActionType.CompleteTransaction:
      return <CompleteTransaction {...props} />
    case ConversationActionType.CompleteDeliveredTransaction:
      return <CompleteDeliveredTransaction {...props} />
    case ConversationActionType.ReportProblem:
      return <ReportProblem {...props} />
    case ConversationActionType.LeaveFeedback:
      return <LeaveFeedback {...props} />
    case ConversationActionType.UpdateBundle:
      return <UpdateBundle {...props} />
    case ConversationActionType.WeHaveMet:
      return <WeHaveMet {...props} />
    case ConversationActionType.RequestReturn:
      return (
        <RequestReturnDetailsProvider>
          <ComplaintActionConversationWrapper>
            {({ conversationTransactionId, conversationTransactionIsBundle }) => (
              <RequestReturn
                {...props}
                providedTransactionId={conversationTransactionId}
                isBundle={conversationTransactionIsBundle}
              />
            )}
          </ComplaintActionConversationWrapper>
        </RequestReturnDetailsProvider>
      )
    case ConversationActionType.ConfirmComplaintResolved:
      return <ConfirmComplaintResolved {...props} />
    case ConversationActionType.RefuseComplaintResolved:
      return <RefuseComplaintResolved {...props} />
    case ConversationActionType.ContactVintedSupport:
      return <ContactVintedSupport {...props} />
    case ConversationActionType.ViewDeliveryInstructions:
      return <ViewDeliveryInstructions {...props} />
    case ConversationActionType.OpenQrCode:
      return <OpenQrCode {...props} />
    case ConversationActionType.RefundProgress:
      return <RefundProgress {...props} />
    case ConversationActionType.NeedHelpSelectChatbotIssue:
    case ConversationActionType.SelectChatbotIssue:
      return <DenyHelpCenterFeedback {...props} />
    case ConversationActionType.ConfirmComplaintResolvedAndLeaveFeedback:
      return isCesModalVisibilityFixEnabled ? (
        <CesFeedbackModal {...props} />
      ) : (
        <ResolveHelpCenterFeedback {...props} />
      )
    case ConversationActionType.LeaveFeedbackForAgentSupport:
      return <ResolveHelpCenterFeedback {...props} />
    case ConversationActionType.SupportRequestResolvedAutoReply:
    case ConversationActionType.SupportRequestResolvedChatbot:
      return <ProvideAutomatedSupportFeedback {...props} />
    case ConversationActionType.SupportRequestNotResolvedAutoReply:
    case ConversationActionType.SupportRequestNotResolvedChatbot:
      return <DenyAutomatedSupportFeedback {...props} />
    case ConversationActionType.ActivateBalance:
      return <ActivateBalance {...props} />
    case ConversationActionType.ReturnOrder:
      return <GetShippingLabel {...props} />
    case ConversationActionType.MarkAsReturnedToSender:
      return <MarkAsReturnedToSender {...props} />
    case ConversationActionType.AppealTheDecision:
      return <AppealTheDecision {...props} />
    case ConversationActionType.UploadItem:
      return <UploadItem {...props} />
    case ConversationActionType.ExtendShippingDeadline:
      return <ExtendShippingDeadline {...props} />
    case ConversationActionType.CancelShippingDeadlineExtension:
      return <CancelShippingDeadlineExtension {...props} />
    case ConversationActionType.AgreeExtendShippingDeadline:
      return <AgreeExtendShippingDeadline {...props} />
    case ConversationActionType.DeclineShippingDeadlineExtension:
      return <DeclineShippingDeadlineExtension {...props} />
    case ConversationActionType.PromotedClosetPrecheckout:
      return <PromotedClosetPrecheckout {...props} />
    case ConversationActionType.PromotedClosetStats:
      return <PromotedClosetStats {...props} />
    case ConversationActionType.CancelChangeDeliveryOptionRequest:
      return <CancelChangeDeliveryOptionRequest {...props} />
    case ConversationActionType.AcceptChangePickupDeliveryOptionRequest:
    case ConversationActionType.AcceptChangeHomeDeliveryOptionRequest:
      return <AcceptChangeDeliveryOptionRequest {...props} />
    case ConversationActionType.CancelRefundOffer:
      return (
        <ComplaintActionConversationWrapper>
          {({ transactionId, complaintId }) => (
            <CancelPartialRefundOffer
              {...props}
              transactionId={transactionId?.toString()}
              complaintId={complaintId}
            />
          )}
        </ComplaintActionConversationWrapper>
      )
    case ConversationActionType.AcceptRefundOffer:
      return <AcceptPartialRefundOffer {...props} />
    case ConversationActionType.RejectRefundOffer:
      return <DeclinePartialRefundOffer {...props} />
    case ConversationActionType.ChooseResolution:
      return (
        <ComplaintActionConversationWrapper>
          {({ transactionId }) => (
            <ChooseComplaintResolution {...props} transactionId={transactionId?.toString()} />
          )}
        </ComplaintActionConversationWrapper>
      )

    default:
      return null
  }
}

export default ConversationAction
